/**
 * Accommodation Service
 * Handles all accommodation provider and booking operations
 */

import { supabase } from './supabaseService';
import {
  AccommodationProvider,
  AccommodationBookingRequest,
  BookingRequestStatus,
  AvailabilityResponse,
  AccommodationCommunication,
  InventoryHold,
  PaymentAuthorization,
  TourAccommodation
} from '../types/accommodation';
import { Location } from '../types';
import { apiService } from './apiService';
import { USE_BACKEND_API } from '../config/apiConfig';

class AccommodationService {
  // Caching for accommodations
  private readonly CACHE_KEY = 'accommodations_cache';
  private readonly CACHE_TIMESTAMP_KEY = 'accommodations_cache_timestamp';
  private readonly CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hour cache
  private cache: AccommodationProvider[] = [];
  private cacheTimestamp: number = 0;
  private pendingRequest: Promise<AccommodationProvider[]> | null = null;
  private tourProvidersCache: Map<string, { providers: AccommodationProvider[]; timestamp: number }> = new Map();
  private readonly TOUR_CACHE_DURATION = 30 * 60 * 1000; // 30 minute cache for tour-specific providers

  constructor() {
    // Load from localStorage immediately for instant response
    this.loadCacheFromStorage();
    if (this.cache.length > 0) {
      console.log(`🚀 AccommodationService initialized - loaded ${this.cache.length} providers from cache`);
    }
  }

  // Load cache from localStorage
  private loadCacheFromStorage(): void {
    try {
      const cachedData = localStorage.getItem(this.CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);

      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();

        if (now - timestamp < this.CACHE_DURATION) {
          this.cache = JSON.parse(cachedData);
          this.cacheTimestamp = timestamp;
          console.log('📦 Loaded accommodations from localStorage cache');
        }
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
    }
  }

  // Save cache to localStorage
  private saveCacheToStorage(providers: AccommodationProvider[]): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(providers));
      localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error saving cache to localStorage:', error);
    }
  }

  // Check if cache is valid
  private isCacheValid(): boolean {
    if (this.cache.length > 0 && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
      return true;
    }

    if (this.cache.length === 0) {
      this.loadCacheFromStorage();
      return this.cache.length > 0 && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
    }

    return false;
  }

  // Clear cache
  private clearCache(): void {
    this.cache = [];
    this.cacheTimestamp = 0;
    this.tourProvidersCache.clear();
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
  }

  /**
   * Get all accommodation providers for a tour (via junction table)
   */
  async getProvidersByTour(tourId: string): Promise<AccommodationProvider[]> {
    // Check tour-specific cache first
    const cached = this.tourProvidersCache.get(tourId);
    if (cached && (Date.now() - cached.timestamp) < this.TOUR_CACHE_DURATION) {
      return [...cached.providers];
    }
    try {
      const { data, error } = await supabase
        .from('tour_accommodations')
        .select(`
          accommodation_provider_id,
          priority,
          recommended,
          accommodation_providers (*)
        `)
        .eq('tour_id', tourId)
        .order('priority', { ascending: true })
        .order('recommended', { ascending: false });

      if (error) {
        console.error('Error fetching providers by tour:', error);
        return [];
      }

      // Extract accommodation providers from the joined data
      const providers = (data || [])
        .map((item: any) => item.accommodation_providers)
        .filter((p: any) => p && p.status === 'active')
        .map((p: any) => this.mapDbToProvider(p));

      // Cache tour-specific providers
      this.tourProvidersCache.set(tourId, {
        providers,
        timestamp: Date.now()
      });

      return providers;
    } catch (error) {
      console.error('Error in getProvidersByTour:', error);
      return [];
    }
  }

  /**
   * Get accommodations by location (name, region, or country)
   */
  async getProvidersByLocation(locationQuery: {
    name?: string;
    region?: string;
    country?: string;
  }): Promise<AccommodationProvider[]> {
    try {
      let query = supabase
        .from('accommodation_providers')
        .select('*')
        .eq('status', 'active');

      // Filter by location name
      if (locationQuery.name) {
        query = query.or(`location_name.ilike.%${locationQuery.name}%,location->>name.ilike.%${locationQuery.name}%`);
      }

      // Filter by region (from location JSONB)
      if (locationQuery.region) {
        query = query.eq('location->>region', locationQuery.region);
      }

      // Filter by country (from location JSONB)
      if (locationQuery.country) {
        query = query.eq('location->>country', locationQuery.country);
      }

      const { data, error } = await query.order('name', { ascending: true });

      if (error) {
        console.error('Error fetching providers by location:', error);
        return [];
      }

      return (data || []).map(this.mapDbToProvider);
    } catch (error) {
      console.error('Error in getProvidersByLocation:', error);
      return [];
    }
  }

  /**
   * Find accommodations near a tour location (by location name matching)
   */
  async findAccommodationsNearTour(tourLocation: Location): Promise<AccommodationProvider[]> {
    try {
      // Search by location name, region, or country
      const { data, error } = await supabase
        .from('accommodation_providers')
        .select('*')
        .eq('status', 'active')
        .or(`location_name.ilike.%${tourLocation.name}%,location->>name.ilike.%${tourLocation.name}%,location->>region.ilike.%${tourLocation.region}%,location->>country.ilike.%${tourLocation.country}%`)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error finding accommodations near tour:', error);
        return [];
      }

      return (data || []).map(this.mapDbToProvider);
    } catch (error) {
      console.error('Error in findAccommodationsNearTour:', error);
      return [];
    }
  }

  /**
   * Find closest accommodations by coordinates (distance-based)
   * Returns accommodations sorted by distance, limited to closest N
   */
  async findClosestAccommodations(
    coordinates: { lat: number; lng: number },
    limit: number = 15
  ): Promise<Array<AccommodationProvider & { distance: number }>> {
    try {
      // Use cached accommodations if available
      let accommodations: AccommodationProvider[];

      if (this.isCacheValid() && this.cache.length > 0) {
        // Filter active accommodations from cache
        accommodations = this.cache.filter(acc => acc.status === 'active');
      } else {
        // Fetch from database if cache is empty
        const { data, error } = await supabase
          .from('accommodation_providers')
          .select('*')
          .eq('status', 'active');

        if (error) {
          console.error('Error fetching accommodations for distance calculation:', error);
          return [];
        }

        accommodations = (data || []).map(this.mapDbToProvider);

        // Update cache
        this.cache = accommodations;
        this.cacheTimestamp = Date.now();
        this.saveCacheToStorage(accommodations);
      }

      // Filter accommodations that have valid coordinates
      const accommodationsWithCoords = accommodations.filter(acc =>
        acc.location?.coordinates?.lat &&
        acc.location?.coordinates?.lng
      );

      // Import distance calculator
      const { getClosestAccommodations } = await import('../utils/distanceCalculator');

      // Get closest accommodations
      return getClosestAccommodations(
        accommodationsWithCoords,
        coordinates,
        limit
      );
    } catch (error) {
      console.error('Error in findClosestAccommodations:', error);
      return [];
    }
  }

  /**
   * Get all accommodation providers (with caching)
   */
  async getAllProviders(forceRefresh: boolean = false): Promise<AccommodationProvider[]> {
    if (USE_BACKEND_API) {
      if (!forceRefresh && this.isCacheValid()) {
        return [...this.cache];
      }

      try {
        const providers = await apiService.get<any[]>('/accommodations');
        const mappedProviders = providers.map((p: any) => this.mapDbToProvider(p));
        
        this.cache = mappedProviders;
        this.cacheTimestamp = Date.now();
        this.saveCacheToStorage(mappedProviders);
        
        return mappedProviders;
      } catch (error) {
        console.error('Error in getAllProviders (Backend):', error);
        return this.cache.length > 0 ? [...this.cache] : [];
      }
    }

    if (!forceRefresh && this.isCacheValid()) {
      return [...this.cache];
    }

    return this.fetchAllProvidersFromDatabase();
  }

  /**
   * Fetch all providers from database (Supabase - Legacy)
   */
  private async fetchAllProvidersFromDatabase(): Promise<AccommodationProvider[]> {
    try {
      const { data, error } = await supabase
        .from('accommodation_providers')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching providers:', error);
        return this.cache.length > 0 ? [...this.cache] : [];
      }

      const providers = (data || []).map((p: any) => this.mapDbToProvider(p));

      // Update cache
      this.cache = providers;
      this.cacheTimestamp = Date.now();
      this.saveCacheToStorage(providers);

      return providers;
    } catch (error) {
      console.error('Error in fetchAllProvidersFromDatabase:', error);
      return this.cache.length > 0 ? [...this.cache] : [];
    }
  }

  /**
   * Get provider by ID
   */
  async getProviderById(id: string): Promise<AccommodationProvider | null> {
    if (USE_BACKEND_API) {
      try {
        const data = await apiService.get<any>(`/accommodations/${id}`);
        return data ? this.mapDbToProvider(data) : null;
      } catch (error) {
        console.error('Error in getProviderById (Backend):', error);
        return null;
      }
    }

    try {
      const { data, error } = await supabase
        .from('accommodation_providers')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching provider:', error);
        return null;
      }

      return this.mapDbToProvider(data);
    } catch (error) {
      console.error('Error in getProviderById:', error);
      return null;
    }
  }

  /**
   * Create accommodation provider
   */
  async createProvider(provider: Omit<AccommodationProvider, 'id' | 'createdAt' | 'updatedAt'>): Promise<AccommodationProvider | null> {
    if (USE_BACKEND_API) {
      try {
        const dbData = this.mapProviderToDb(provider);
        const data = await apiService.post<any>('/accommodations', dbData);
        const newProvider = this.mapDbToProvider(data);
        this.clearCache();
        return newProvider;
      } catch (error) {
        console.error('Error in createProvider (Backend):', error);
        throw error;
      }
    }

    try {
      const dbData = this.mapProviderToDb(provider);

      const { data, error } = await supabase
        .from('accommodation_providers')
        .insert(dbData)
        .select()
        .single();

      if (error) {
        console.error('Error creating provider:', error);
        throw error;
      }

      const newProvider = this.mapDbToProvider(data);

      // Clear cache to force refresh
      this.clearCache();

      return newProvider;
    } catch (error) {
      console.error('Error in createProvider:', error);
      throw error;
    }
  }

  /**
   * Update accommodation provider
   */
  async updateProvider(id: string, providerData: Partial<AccommodationProvider>): Promise<AccommodationProvider | null> {
    if (USE_BACKEND_API) {
      try {
        const dbData = this.mapProviderToDb(providerData as any);
        const data = await apiService.put<any>(`/accommodations/${id}`, dbData);
        this.clearCache();
        return this.mapDbToProvider(data);
      } catch (error) {
        console.error('Error in updateProvider (Backend):', error);
        throw error;
      }
    }

    try {
      const dbData = this.mapProviderToDb(providerData as any);
      const { data, error } = await supabase
        .from('accommodation_providers')
        .update(dbData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating provider:', error);
        throw error;
      }

      this.clearCache();
      return this.mapDbToProvider(data);
    } catch (error) {
      console.error('Error in updateProvider:', error);
      throw error;
    }
  }

  /**
   * Delete accommodation provider
   */
  async deleteProvider(id: string): Promise<boolean> {
    if (USE_BACKEND_API) {
      try {
        await apiService.delete(`/accommodations/${id}`);
        this.clearCache();
        return true;
      } catch (error) {
        console.error('Error in deleteProvider (Backend):', error);
        throw error;
      }
    }

    try {
      const { error } = await supabase
        .from('accommodation_providers')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting provider:', error);
        throw error;
      }

      this.clearCache();
      return true;
    } catch (error) {
      console.error('Error in deleteProvider:', error);
      throw error;
    }
  }

  /**
   * Associate accommodation with a tour
   */
  async associateAccommodationToTour(
    tourId: string,
    accommodationProviderId: string,
    options?: {
      priority?: number;
      recommended?: boolean;
      notes?: string;
    }
  ): Promise<TourAccommodation | null> {
    try {
      const { data, error } = await supabase
        .from('tour_accommodations')
        .insert({
          tour_id: tourId,
          accommodation_provider_id: accommodationProviderId,
          priority: options?.priority ?? 0,
          recommended: options?.recommended ?? false,
          notes: options?.notes
        })
        .select()
        .single();

      if (error) {
        console.error('Error associating accommodation to tour:', error);
        throw error;
      }

      return this.mapDbToTourAccommodation(data);
    } catch (error) {
      console.error('Error in associateAccommodationToTour:', error);
      throw error;
    }
  }

  /**
   * Remove accommodation association from tour
   */
  async removeAccommodationFromTour(tourId: string, accommodationProviderId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('tour_accommodations')
        .delete()
        .eq('tour_id', tourId)
        .eq('accommodation_provider_id', accommodationProviderId);

      if (error) {
        console.error('Error removing accommodation from tour:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in removeAccommodationFromTour:', error);
      return false;
    }
  }

  /**
   * Update tour-accommodation association
   */
  async updateTourAccommodation(
    id: string,
    updates: {
      priority?: number;
      recommended?: boolean;
      notes?: string;
    }
  ): Promise<TourAccommodation | null> {
    try {
      const { data, error } = await supabase
        .from('tour_accommodations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating tour accommodation:', error);
        throw error;
      }

      return this.mapDbToTourAccommodation(data);
    } catch (error) {
      console.error('Error in updateTourAccommodation:', error);
      throw error;
    }
  }

  /**
   * Get all tour-accommodation associations for a tour
   */
  async getTourAccommodations(tourId: string): Promise<TourAccommodation[]> {
    try {
      const { data, error } = await supabase
        .from('tour_accommodations')
        .select(`
          *,
          accommodation_providers (*)
        `)
        .eq('tour_id', tourId)
        .order('priority', { ascending: true })
        .order('recommended', { ascending: false });

      if (error) {
        console.error('Error fetching tour accommodations:', error);
        return [];
      }

      return (data || []).map((item: any) => ({
        ...this.mapDbToTourAccommodation(item),
        provider: item.accommodation_providers ? this.mapDbToProvider(item.accommodation_providers) : undefined
      }));
    } catch (error) {
      console.error('Error in getTourAccommodations:', error);
      return [];
    }
  }

  /**
   * Create booking request
   */
  async createBookingRequest(
    request: Omit<AccommodationBookingRequest, 'id' | 'createdAt' | 'updatedAt' | 'version'>
  ): Promise<AccommodationBookingRequest | null> {
    try {
      // Generate idempotency key
      const idempotencyKey = `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const dbData = {
        safari_booking_id: request.safariBookingId,
        client_id: request.clientId,
        provider_id: request.providerId,
        tour_id: request.tourId,
        location: request.location,
        client_name: request.clientName,
        client_email: request.clientEmail,
        client_phone: request.clientPhone,
        client_nationality: request.clientNationality,
        client_special_requests: request.clientSpecialRequests,
        provider_contact_name: request.providerContactName,
        provider_contact_email: request.providerContactEmail,
        provider_contact_phone: request.providerContactPhone,
        group_name: request.groupName,
        guest_count: request.guestCount,
        arrival_date: request.arrivalDate,
        departure_date: request.departureDate,
        nights: request.nights,
        room_type: request.roomType,
        meal_plan: request.mealPlan,
        provider_special_requests: request.providerSpecialRequests,
        status: request.status,
        availability_response: request.availabilityResponse,
        confirmation_number: request.confirmationNumber,
        requested_rooms: request.requestedRooms,
        confirmed_rooms: request.confirmedRooms,
        requested_nights: request.requestedNights,
        confirmed_nights: request.confirmedNights,
        partial_confirmation_details: request.partialConfirmationDetails,
        idempotency_key: idempotencyKey,
        version: 1,
        payment_authorized: request.paymentAuthorized,
        payment_captured: request.paymentCaptured,
        payment_authorization_id: request.paymentAuthorizationId,
        payment_amount: request.paymentAmount,
        payment_currency: request.paymentCurrency,
        last_communication_at: request.lastCommunicationAt,
        response_deadline: request.responseDeadline,
      };

      const { data, error } = await supabase
        .from('accommodation_booking_requests')
        .insert(dbData)
        .select()
        .single();

      if (error) {
        console.error('Error creating booking request:', error);
        throw error;
      }

      return this.mapDbToBookingRequest(data);
    } catch (error) {
      console.error('Error in createBookingRequest:', error);
      throw error;
    }
  }

  /**
   * Get booking requests for a client
   */
  async getBookingRequestsByClient(clientId: string): Promise<AccommodationBookingRequest[]> {
    try {
      const { data, error } = await supabase
        .from('accommodation_booking_requests')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching booking requests:', error);
        return [];
      }

      return (data || []).map(this.mapDbToBookingRequest);
    } catch (error) {
      console.error('Error in getBookingRequestsByClient:', error);
      return [];
    }
  }

  /**
   * Get booking requests for a safari booking
   */
  async getBookingRequestsBySafari(safariBookingId: string): Promise<AccommodationBookingRequest[]> {
    try {
      const { data, error } = await supabase
        .from('accommodation_booking_requests')
        .select('*')
        .eq('safari_booking_id', safariBookingId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching booking requests:', error);
        return [];
      }

      return (data || []).map(this.mapDbToBookingRequest);
    } catch (error) {
      console.error('Error in getBookingRequestsBySafari:', error);
      return [];
    }
  }

  /**
   * Update booking request status (with state machine validation)
   */
  async updateBookingRequestStatus(
    id: string,
    newStatus: BookingRequestStatus,
    changedBy: string,
    reason?: string
  ): Promise<AccommodationBookingRequest | null> {
    try {
      // Get current booking
      const current = await supabase
        .from('accommodation_booking_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (current.error || !current.data) {
        throw new Error('Booking request not found');
      }

      const currentStatus = current.data.status as BookingRequestStatus;

      // Validate state transition (basic validation - can be enhanced)
      if (!this.canTransitionStatus(currentStatus, newStatus)) {
        throw new Error(`Invalid status transition from ${currentStatus} to ${newStatus}`);
      }

      // Update with optimistic locking
      const { data, error } = await supabase
        .from('accommodation_booking_requests')
        .update({
          status: newStatus,
          version: current.data.version + 1,
          updated_by: changedBy,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('version', current.data.version) // Optimistic lock
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('Booking was modified by another process. Please refresh and try again.');
        }
        throw error;
      }

      return this.mapDbToBookingRequest(data);
    } catch (error) {
      console.error('Error updating booking request status:', error);
      throw error;
    }
  }

  /**
   * State machine validation
   */
  private canTransitionStatus(from: BookingRequestStatus, to: BookingRequestStatus): boolean {
    const validTransitions: Record<BookingRequestStatus, BookingRequestStatus[]> = {
      draft: ['pending'],
      pending: ['sent', 'cancelled'],
      sent: ['awaiting_response', 'acknowledged'],
      acknowledged: ['quoted', 'confirmed', 'declined'],
      quoted: ['confirmed', 'declined', 'expired'],
      awaiting_response: ['confirmed', 'partial_confirmed', 'rejected', 'failed', 'manual_review'],
      partial_confirmed: ['confirmed', 'rejected', 'manual_review'],
      confirmed: ['cancellation_requested', 'cancelled', 'completed'],
      declined: ['alternative_suggested', 'failed'],
      expired: ['manual_review', 'cancelled'],
      rejected: ['alternative_suggested', 'failed'],
      alternative_suggested: ['confirmed'],
      cancelled: [],
      cancellation_requested: ['cancelled'],
      payment_pending: ['payment_failed', 'confirmed'],
      payment_failed: ['manual_review', 'cancelled'],
      completed: [],
      failed: ['manual_review', 'rejected'],
      manual_review: ['confirmed', 'rejected', 'cancelled']
    };

    return validTransitions[from]?.includes(to) ?? false;
  }

  /**
   * Map database row to AccommodationProvider
   */
  private mapDbToProvider(dbRow: any): AccommodationProvider {
    // Handle location - prefer JSONB location, fallback to legacy fields
    let location: AccommodationProvider['location'];
    if (dbRow.location && typeof dbRow.location === 'object') {
      // Use JSONB location if available
      location = {
        name: dbRow.location.name || dbRow.location_name || 'Unknown',
        country: dbRow.location.country || 'Kenya',
        region: dbRow.location.region || 'East Africa',
        coordinates: dbRow.location.coordinates || dbRow.coordinates || { lat: 0, lng: 0 },
        address: dbRow.location.address || dbRow.address || ''
      };
    } else {
      // Fallback to legacy fields
      location = {
        name: dbRow.location_name || 'Unknown',
        country: 'Kenya',
        region: 'East Africa',
        coordinates: dbRow.coordinates || { lat: 0, lng: 0 },
        address: dbRow.address || ''
      };
    }

    return {
      id: dbRow.id,
      name: dbRow.name,
      type: dbRow.type || 'hotel',
      locationName: dbRow.locationName || dbRow.location_name,
      location: dbRow.location || location,
      rating: dbRow.rating ? parseFloat(dbRow.rating) : 0,
      priceRange: dbRow.priceRange || { min: 0, max: 0 },
      amenities: dbRow.amenities || [],
      images: dbRow.images || ((dbRow.imageUrl || dbRow.image_url) ? [dbRow.imageUrl || dbRow.image_url] : []),
      imageUrl: dbRow.imageUrl || dbRow.image_url || '',
      description: dbRow.description || '',
      contactInfo: dbRow.contactInfo || { 
        phone: dbRow.phone || dbRow.contactPhone || '', 
        email: dbRow.primaryEmail || dbRow.contactEmail || '', 
        website: dbRow.website || '' 
      },
      availability: dbRow.active !== undefined ? dbRow.active : (dbRow.availability !== undefined ? dbRow.availability : true),
      capacity: dbRow.capacity || { rooms: 0, maxGuests: 0 },
      // Legacy fields for backward compatibility
      coordinates: dbRow.coordinates,
      address: dbRow.address,
      primaryEmail: dbRow.primaryEmail || dbRow.contactEmail,
      phone: dbRow.phone || dbRow.contactPhone,
      currency: dbRow.currency || 'USD',
      status: dbRow.active || dbRow.status === 'active' ? 'active' : 'inactive',
      createdAt: dbRow.createdAt || dbRow.created_at,
      updatedAt: dbRow.updatedAt || dbRow.updated_at
    };
  }

  /**
   * Map AccommodationProvider to database row
   */
  private mapProviderToDb(provider: Omit<AccommodationProvider, 'id' | 'createdAt' | 'updatedAt'>): any {
    // Only return fields that exist in the Prisma model to prevent 500 errors
    // Note: Prisma Client uses camelCase field names even if they are @mapped to snake_case in the DB
    return {
      name: provider.name,
      type: provider.type,
      locationName: provider.locationName || provider.location?.name,
      coordinates: provider.location?.coordinates || provider.coordinates || { lat: 0, lng: 0 },
      rating: parseFloat(String(provider.rating || 0)) || 0,
      contactEmail: (provider as any).contactInfo?.email || (provider as any).primaryEmail || (provider as any).contactEmail || '',
      contactPhone: (provider as any).contactInfo?.phone || (provider as any).phone || (provider as any).contactPhone || '',
      phone: (provider as any).phone || (provider as any).contactInfo?.phone || '',
      price: parseFloat(String((provider as any).basePricePerNight || (provider as any).price || 0)) || 0,
      website: (provider as any).contactInfo?.website || (provider as any).website || '',
      address: provider.address || provider.location?.address || '',
      amenities: Array.isArray(provider.amenities) ? provider.amenities : [],
      capacity: (provider.capacity && typeof provider.capacity === 'object') ? (Number(provider.capacity.maxGuests) || 0) : (Number(provider.capacity) || 0),
      description: provider.description || '',
      imageUrl: (provider as any).imageUrl || (provider as any).image_url || '',
      images: Array.isArray(provider.images) ? provider.images : [],
      isFeatured: Boolean((provider as any).isFeatured),
      active: provider.status === 'active' || (provider as any).availability !== false
    };
  }

  /**
   * Map database row to AccommodationBookingRequest
   */
  private mapDbToBookingRequest(dbRow: any): AccommodationBookingRequest {
    return {
      id: dbRow.id,
      safariBookingId: dbRow.safari_booking_id,
      clientId: dbRow.client_id,
      providerId: dbRow.provider_id,
      tourId: dbRow.tour_id,
      location: dbRow.location,
      clientName: dbRow.client_name,
      clientEmail: dbRow.client_email,
      clientPhone: dbRow.client_phone,
      clientNationality: dbRow.client_nationality,
      clientSpecialRequests: dbRow.client_special_requests,
      providerContactName: dbRow.provider_contact_name,
      providerContactEmail: dbRow.provider_contact_email,
      providerContactPhone: dbRow.provider_contact_phone,
      groupName: dbRow.group_name,
      groupSize: dbRow.group_size || dbRow.guest_count || 1,
      guestCount: dbRow.guest_count,
      checkInDate: dbRow.check_in_date || dbRow.arrival_date,
      checkOutDate: dbRow.check_out_date || dbRow.departure_date,
      arrivalDate: dbRow.arrival_date,
      departureDate: dbRow.departure_date,
      nights: dbRow.nights,
      roomRequirements: dbRow.room_requirements || dbRow.room_type || 'standard',
      roomType: dbRow.room_type,
      mealPlan: dbRow.meal_plan,
      providerSpecialRequests: dbRow.provider_special_requests,
      status: dbRow.status as BookingRequestStatus,
      priority: dbRow.priority || 'medium',
      responseDeadline: dbRow.response_deadline,
      availabilityResponse: dbRow.availability_response,
      confirmationNumber: dbRow.confirmation_number,
      requestedRooms: dbRow.requested_rooms,
      confirmedRooms: dbRow.confirmed_rooms,
      requestedNights: dbRow.requested_nights,
      confirmedNights: dbRow.confirmed_nights,
      partialConfirmationDetails: dbRow.partial_confirmation_details,
      idempotencyKey: dbRow.idempotency_key,
      version: dbRow.version,
      lockUntil: dbRow.lock_until,
      lockedBy: dbRow.locked_by,
      paymentAuthorized: dbRow.payment_authorized,
      paymentCaptured: dbRow.payment_captured,
      paymentAuthorizationId: dbRow.payment_authorization_id,
      paymentAmount: dbRow.payment_amount ? parseFloat(dbRow.payment_amount) : undefined,
      paymentCurrency: dbRow.payment_currency,
      lastCommunicationAt: dbRow.last_communication_at,
      createdAt: dbRow.created_at,
      updatedAt: dbRow.updated_at
    };
  }

  /**
   * Map database row to TourAccommodation
   */
  private mapDbToTourAccommodation(dbRow: any): TourAccommodation {
    return {
      id: dbRow.id,
      tourId: dbRow.tour_id,
      providerId: dbRow.accommodation_provider_id,
      provider: {} as AccommodationProvider, // This would be populated by a join query
      isDefault: dbRow.is_default || false,
      priority: dbRow.priority || 0,
      priceOverride: dbRow.price_override ? parseFloat(dbRow.price_override) : undefined,
      specialTerms: dbRow.special_terms,
      createdAt: dbRow.created_at,
      updatedAt: dbRow.updated_at
    };
  }
}

export const accommodationService = new AccommodationService();

